import {
    CourseInfo,
    CourseTrack,
    ReleaseStatus,
} from 'common/src/api/core/courses/types'
import { sortCourses } from '@/utils/sortCourses'

// Marketing-level grouping of exams shown to visitors (Health / Life /
// Cross-Practice). Derived from each course's track, so a new course lands in
// the right group as soon as its track is set in Content-Backend. SOA
// cross-practice courses (CP 3xx) count toward several practice areas, so
// they are grouped by their "CP" exam code instead of their track.
export enum CourseOffering {
    health = 'Health',
    life = 'Life',
    crossPractice = 'Cross-Practice',
}

const TRACK_TO_OFFERING: Partial<Record<string, CourseOffering>> = {
    [CourseTrack.FSA_HEALTH]: CourseOffering.health,
    'FCIA - Group Benefits': CourseOffering.health, // CourseTrack.FCIA_GROUP_BENEFITS (newer common)
    [CourseTrack.FSA_LIFE]: CourseOffering.life,
}

const OFFERING_ORDER: string[] = [
    CourseOffering.health,
    CourseOffering.life,
    CourseOffering.crossPractice,
]

export interface CourseOfferingGroup {
    // a CourseOffering, or the raw track for tracks with no offering yet
    offering: string
    courses: CourseInfo[]
}

export const getOfferingForCourse = (courseInfo: CourseInfo): string => {
    if (courseInfo.abbreviation.startsWith('CP')) {
        return CourseOffering.crossPractice
    }
    return TRACK_TO_OFFERING[courseInfo.track] ?? courseInfo.track
}

export const groupCoursesByOffering = (
    coursesInfo: CourseInfo[]
): CourseOfferingGroup[] => {
    const groups = new Map<string, CourseInfo[]>()
    for (const courseInfo of coursesInfo) {
        const offering = getOfferingForCourse(courseInfo)
        groups.set(offering, [...(groups.get(offering) ?? []), courseInfo])
    }
    const orderOf = (offering: string): number => {
        const index = OFFERING_ORDER.indexOf(offering)
        return index === -1 ? OFFERING_ORDER.length : index
    }
    return [...groups.entries()]
        .sort(([a], [b]) => orderOf(a) - orderOf(b) || a.localeCompare(b))
        .map(([offering, courses]) => ({
            offering,
            courses: [...courses].sort(sortCourses),
        }))
}

// e.g. "FSA Health, Life and Cross-Practice" for the splash page subtitle
export const getSplashPageExamsLabel = (coursesInfo: CourseInfo[]): string => {
    const offerings = groupCoursesByOffering(
        coursesInfo.filter(
            (courseInfo) =>
                courseInfo.releaseInfo.releaseStatus ===
                ReleaseStatus.fullyAvailable
        )
    ).map((group) => group.offering)
    if (!offerings.length) {
        return 'FSA actuarial'
    }
    if (offerings.length === 1) {
        return `FSA ${offerings[0]}`
    }
    return `FSA ${offerings.slice(0, -1).join(', ')} and ${offerings.at(-1)}`
}
